import React from "react";
import { EyeOffIcon } from "@components/Common/SvgIcons/EyeOffIcon";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { useTranslation } from "react-i18next";

import { ACTION_ICONS } from "../View";
import { ActionItem } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputLabel,
    Input,
    InputWrapper,
    ResetButton,
    VisibilityButton,
    ActionConfigCard,
    ActionConfigHeader,
    ActionConfigTitle,
    ActionConfigControls,
    ActionConfigBody,
    ActionFieldRow,
    ActionTextRow,
    ResetAllButton,
    IconCircle,
    IconPickerWrapper,
    IconPickerButton,
    IconPickerDropdown,
    IconPickerGrid,
    IconPickerItem,
    ColorPickerRow,
    ColorInput,
    ColorLabel,
} from "./styled";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type ActionsTableProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedButtonsLang: string;
    onButtonsLangChange: (lang: string) => void;
    actions: ActionItem[];
    defaultActions: ActionItem[];
    iconPickerOpen: number | null;
    iconPickerRef: React.RefObject<HTMLDivElement>;
    onSetIconPickerOpen: (id: number | null) => void;
    onToggleActionActive: (id: number) => void;
    onToggleActionTitleVisible: (id: number) => void;
    onToggleActionDescVisible: (id: number) => void;
    onUpdateActionField: (
        id: number,
        field: "title" | "description",
        value: string
    ) => void;
    onUpdateActionIcon: (id: number, iconKey: string) => void;
    onUpdateActionColor: (id: number, color: string) => void;
    onResetActionTitle: (id: number) => void;
    onResetActionDescription: (id: number) => void;
    onResetActionTexts: (id: number) => void;
    onResetAction: (id: number) => void;
    onResetAllActions: () => void;
};

export function ActionsTable({
    mainColor,
    languages,
    selectedButtonsLang,
    onButtonsLangChange,
    actions,
    defaultActions,
    iconPickerOpen,
    iconPickerRef,
    onSetIconPickerOpen,
    onToggleActionActive,
    onToggleActionTitleVisible,
    onToggleActionDescVisible,
    onUpdateActionField,
    onUpdateActionIcon,
    onUpdateActionColor,
    onResetActionTitle,
    onResetActionDescription,
    onResetActionTexts,
    onResetAction,
    onResetAllActions,
}: ActionsTableProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card>
            <SectionHeader>
                <SectionTitle>{t("Action buttons")}</SectionTitle>
                <ResetButton
                    data-testid="scan-to-actions-reset-all-actions"
                    mainColor={mainColor}
                    onClick={onResetAllActions}
                    title={t("Reset all")}
                    aria-label={t("Reset all")}
                >
                    <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                </ResetButton>
            </SectionHeader>

            <LanguageTabs data-testid="scan-to-actions-buttons-lang-tabs">
                {languages.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-actions-buttons-lang-${lang.code}`}
                        isActive={selectedButtonsLang === lang.code}
                        mainColor={mainColor}
                        onClick={() => onButtonsLangChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                }}
            >
                {[...actions]
                    .sort((a, b) => a.order - b.order)
                    .map((action) => (
                        <ActionConfigCard
                            key={action.id}
                            data-testid={`scan-to-actions-card-${action.id}`}
                            isActive={action.active}
                        >
                            <ActionConfigHeader>
                                <ActionConfigTitle>
                                    <IconCircle
                                        bgColor={action.iconColor}
                                        size={30}
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path
                                                d={
                                                    ACTION_ICONS[action.icon] ||
                                                    ACTION_ICONS.restaurant
                                                }
                                            />
                                        </svg>
                                    </IconCircle>
                                    {action.title[selectedButtonsLang] ||
                                        action.title.fr}
                                </ActionConfigTitle>
                                <ActionConfigControls>
                                    <ResetAllButton
                                        type="button"
                                        data-testid={`scan-to-actions-reset-${action.id}`}
                                        mainColor={mainColor}
                                        onClick={() => onResetAction(action.id)}
                                        title={t("Reset")}
                                        aria-label={t("Reset")}
                                    >
                                        <ResetIcon
                                            style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }}
                                        />
                                    </ResetAllButton>
                                    <ToggleContainer>
                                        <ToggleLabel style={{ fontSize: 12 }}>
                                            {t("Active")}
                                        </ToggleLabel>
                                        <SmallToggle
                                            data-testid={`scan-to-actions-active-toggle-${action.id}`}
                                            isActive={action.active}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onToggleActionActive(action.id)
                                            }
                                            aria-label={t(
                                                "Toggle action visibility"
                                            )}
                                        />
                                    </ToggleContainer>
                                </ActionConfigControls>
                            </ActionConfigHeader>

                            <ActionConfigBody>
                                {/* Icon picker */}
                                <ActionFieldRow>
                                    <InputLabel>{t("Icon")}</InputLabel>
                                    <IconPickerWrapper
                                        ref={
                                            iconPickerOpen === action.id
                                                ? iconPickerRef
                                                : undefined
                                        }
                                    >
                                        <IconPickerButton
                                            data-testid={`scan-to-actions-icon-picker-btn-${action.id}`}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onSetIconPickerOpen(
                                                    iconPickerOpen === action.id
                                                        ? null
                                                        : action.id
                                                )
                                            }
                                        >
                                            <IconCircle
                                                bgColor={action.iconColor}
                                                size={24}
                                            >
                                                <svg viewBox="0 0 24 24">
                                                    <path
                                                        d={
                                                            ACTION_ICONS[
                                                                action.icon
                                                            ] ||
                                                            ACTION_ICONS.restaurant
                                                        }
                                                    />
                                                </svg>
                                            </IconCircle>
                                            {t("Change icon")}
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 24 24"
                                                fill="#666"
                                            >
                                                <path d="M7 10l5 5 5-5z" />
                                            </svg>
                                        </IconPickerButton>

                                        {iconPickerOpen === action.id && (
                                            <IconPickerDropdown>
                                                <IconPickerGrid>
                                                    {Object.entries(
                                                        ACTION_ICONS
                                                    ).map(([key, path]) => (
                                                        <IconPickerItem
                                                            key={key}
                                                            data-testid={`scan-to-actions-icon-option-${action.id}-${key}`}
                                                            isActive={
                                                                action.icon ===
                                                                key
                                                            }
                                                            mainColor={
                                                                mainColor
                                                            }
                                                            onClick={() =>
                                                                onUpdateActionIcon(
                                                                    action.id,
                                                                    key
                                                                )
                                                            }
                                                            title={key}
                                                        >
                                                            <svg viewBox="0 0 24 24">
                                                                <path
                                                                    d={path}
                                                                />
                                                            </svg>
                                                        </IconPickerItem>
                                                    ))}
                                                </IconPickerGrid>
                                                <ColorPickerRow>
                                                    <ColorLabel>
                                                        {t("Icon color")}
                                                    </ColorLabel>
                                                    <ColorInput
                                                        data-testid={`scan-to-actions-color-picker-${action.id}`}
                                                        type="color"
                                                        value={action.iconColor}
                                                        onChange={(e) =>
                                                            onUpdateActionColor(
                                                                action.id,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            color: "#666",
                                                        }}
                                                    >
                                                        {action.iconColor}
                                                    </span>
                                                </ColorPickerRow>
                                            </IconPickerDropdown>
                                        )}
                                    </IconPickerWrapper>
                                </ActionFieldRow>

                                {/* Title & Description header */}
                                <div
                                    style={{
                                        borderTop: "1px solid #e0e0e0",
                                        paddingTop: "10px",
                                        marginTop: "4px",
                                    }}
                                >
                                    <ActionTextRow
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            paddingBottom: "10px",
                                            marginBottom: "0",
                                        }}
                                    >
                                        <InputLabel></InputLabel>
                                        <InputLabel style={{ fontWeight: 500 }}>
                                            {t("Default text")}
                                        </InputLabel>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <InputLabel
                                                style={{
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {t("Customized")}
                                            </InputLabel>
                                            <ResetAllButton
                                                data-testid={`scan-to-actions-reset-texts-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionTexts(
                                                        action.id
                                                    )
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetAllButton>
                                        </div>
                                    </ActionTextRow>

                                    {/* Title row */}
                                    <ActionTextRow>
                                        <InputLabel>{t("Title")}</InputLabel>
                                        <Input
                                            data-testid={`scan-to-actions-default-title-input-${action.id}`}
                                            value={
                                                defaultActions.find(
                                                    (d) => d.id === action.id
                                                )?.title[selectedButtonsLang] ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-actions-title-input-${action.id}`}
                                                value={
                                                    action.title[
                                                        selectedButtonsLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onUpdateActionField(
                                                        action.id,
                                                        "title",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    defaultActions.find(
                                                        (d) =>
                                                            d.id === action.id
                                                    )?.title[
                                                        selectedButtonsLang
                                                    ] || t("Action title")
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-actions-title-visibility-${action.id}`}
                                                isVisible={action.titleVisible}
                                                onClick={() =>
                                                    onToggleActionTitleVisible(
                                                        action.id
                                                    )
                                                }
                                                title={
                                                    action.titleVisible
                                                        ? "Masquer le titre"
                                                        : "Afficher le titre"
                                                }
                                                aria-label={
                                                    action.titleVisible
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-pressed={
                                                    action.titleVisible
                                                }
                                            >
                                                {action.titleVisible ? (
                                                    <EyeIcon />
                                                ) : (
                                                    <EyeOffIcon />
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-actions-reset-title-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionTitle(
                                                        action.id
                                                    )
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </ActionTextRow>

                                    {/* Description row */}
                                    <ActionTextRow>
                                        <InputLabel>
                                            {t("Description")}
                                        </InputLabel>
                                        <Input
                                            data-testid={`scan-to-actions-default-description-input-${action.id}`}
                                            value={
                                                defaultActions.find(
                                                    (d) => d.id === action.id
                                                )?.description[
                                                    selectedButtonsLang
                                                ] || ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-actions-description-input-${action.id}`}
                                                value={
                                                    action.description[
                                                        selectedButtonsLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onUpdateActionField(
                                                        action.id,
                                                        "description",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    defaultActions.find(
                                                        (d) =>
                                                            d.id === action.id
                                                    )?.description[
                                                        selectedButtonsLang
                                                    ] || t("Action description")
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-actions-description-visibility-${action.id}`}
                                                isVisible={
                                                    action.descriptionVisible
                                                }
                                                onClick={() =>
                                                    onToggleActionDescVisible(
                                                        action.id
                                                    )
                                                }
                                                title={
                                                    action.descriptionVisible
                                                        ? "Masquer la description"
                                                        : "Afficher la description"
                                                }
                                                aria-label={
                                                    action.descriptionVisible
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-pressed={
                                                    action.descriptionVisible
                                                }
                                            >
                                                {action.descriptionVisible ? (
                                                    <EyeIcon />
                                                ) : (
                                                    <EyeOffIcon />
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-actions-reset-description-${action.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetActionDescription(
                                                        action.id
                                                    )
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </ActionTextRow>
                                </div>
                            </ActionConfigBody>
                        </ActionConfigCard>
                    ))}
            </div>
        </Card>
    );
}
